import React, { useEffect, useCallback, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import { State, MainThunkDispatch } from "../../../../../Reducers"
import { OrgDashboardBase } from "../DashboardBase/OrgDashboardBase"
import {
    APIDataModel,
    getDataUrl,
    getDataID
} from "../../../../../Models/API/DataModel"
import {
    destroy,
    downloadAllDataUrls,
    getDataList
} from "../../../../../Actions/API/Data/DataActions"
import { Uploader } from "../../../../Common/Render/Common/Uploader/Uploader"
import { DropzoneState } from "react-dropzone"
import {
    UploadStatus,
    UploadQueueItemInProgress,
    UploadQueueItem,
    isInProgress,
    isQueued
} from "../../../../../Models/Client/UploadQueueItem"
import {
    clearSelection,
    toggleSelection
} from "../../../../../Actions/Client/Interface/BatchEditActions"
import { BatchEditSidebar } from "../../../../Common/Sidebar/BatchEditSidebar"
import { Button, ButtonGroup } from "dash-styled"
import {
    downloadAsCSV,
    downloadAsJSON
} from "../../../../../Helpers/Functions/downloadAs"
import { TableColumn, Table } from "resha"
import { TableHeader } from "../../../../Common/Table/TableHeader"
import { SortExpression } from "../../../../../Models/Client/SortExpression"
import { Confirm } from "../../../../Common/Modal/Confirm"
import { NothingMatched } from "../../../../Common/Empty/NothingMatched"
import { useSignedUrlExpiryMonitoring } from "../../../../../Hooks/useSignedUrlExpiryMonitoring"
import { useAsyncCommand } from "../../../../../Hooks/useAsyncCommand"
import { DownloadTypeModal } from "../../Common/Modals/DownloadTypeModal/DownloadTypeModal"
import { DownloadType } from "../../../../../Hooks/useJobListDownload"
import { Thumbnail } from "../../../../Common/Thumbnail/Thumbnail"
import { fromMimeType } from "../../../../../Models/Client/FileType"

const UPLOADER_ID = "datasets_uploader"
const BATCH_EDIT_ID = "datasets"

interface DataSetsProps {
    orgUsername: string
}

interface StateProps {
    list: APIDataModel[]
    pages: number
    pageNumber: number
    sort: SortExpression<APIDataModel>
    loading: boolean
    selectedDataIds: string[]
    isBatchMode: boolean
    uploadQueue: UploadQueueItem[]
    totalItems: number
}

interface DispatchProps {
    getList: (page: number, sort: SortExpression<APIDataModel>) => void
    doDestroy: (paths: string[]) => Promise<void>
    doClearSelection: () => void
    doToggleSelection: (itemIds: string[]) => void
    doDownloadAllDataUrls: (
        fileName: string,
        type: DownloadType
    ) => Promise<void>
}

export type Props = DataSetsProps & StateProps & DispatchProps

const columns: TableColumn<APIDataModel>[] = [
    {
        renderHeader: () => null,
        render: model => {
            return (
                <a
                    href={model.signedUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    <Thumbnail
                        type={
                            model.mimeType === undefined
                                ? undefined
                                : fromMimeType(model.mimeType)
                        }
                        value={model.signedUrl}
                    />
                </a>
            )
        },
        headerClassName: "w-16"
    },
    {
        renderHeader: () => "URL",
        render: model => (
            <a
                href={model.signedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-grape-900"
            >
                {getDataUrl(model)}
            </a>
        ),
        className: "truncate",
        headerClassName: "w-full"
    },
    {
        renderHeader: () => "Created",
        render: ({ created }) => new Date(created).toLocaleString(),
        className: "text-smoke-610",
        headerClassName: "w-48"
    }
]

type Dialog = "delete" | "download" | undefined

export const DataSetsIsolated = ({
    orgUsername,
    list,
    uploadQueue,
    pages,
    pageNumber,
    sort,
    loading,
    getList,
    doDestroy,
    selectedDataIds,
    doClearSelection,
    doToggleSelection,
    isBatchMode,
    doDownloadAllDataUrls,
    totalItems
}: Props) => {
    const [dialog, setDialog] = useState<Dialog>(undefined)
    const hideDialog = useCallback(() => setDialog(undefined), [])
    const confirmDelete = useCallback(() => setDialog("delete"), [])
    const confirmDownload = useCallback(() => setDialog("download"), [])
    const [downloadType, setDownloadType] = useState<DownloadType>("csv")
    const uploadQueueCount = uploadQueue.filter(
        item => isInProgress(item) || isQueued(item)
    ).length
    const prevUploadQueueCount = useRef<number | undefined>(undefined)
    const reloadList = useCallback(() => {
        getList(pageNumber, sort)
    }, [getList, pageNumber, sort])
    const signedUrls = useMemo(
        () =>
            list.flatMap(data =>
                data.signedUrl === undefined ? [] : [data.signedUrl]
            ),
        [list]
    )

    useEffect(reloadList, [reloadList])

    useEffect(() => {
        if (
            prevUploadQueueCount.current !== undefined &&
            prevUploadQueueCount.current > 0 &&
            uploadQueueCount === 0
        ) {
            getList(0, sort)
        }
        prevUploadQueueCount.current = uploadQueueCount
    }, [prevUploadQueueCount, list, uploadQueueCount, getList, sort])

    useSignedUrlExpiryMonitoring(signedUrls, reloadList)

    const { perform: deleteFiles, isProcessing: isDeleting } = useAsyncCommand(
        async () => {
            await doDestroy(
                selectedDataIds.reduce((passed, id) => {
                    const data = list.find(d => String(d.id) === id)

                    if (!data) return passed
                    return passed.concat([data.path])
                }, [] as string[])
            )
            doClearSelection()
            hideDialog()
        }
    )

    const fileName = () => `data_urls_${Date.now()}`

    const { perform: downloadAll, isProcessing: isDownloadingAll } =
        useAsyncCommand(() => doDownloadAllDataUrls(fileName(), downloadType))

    const downloadData = () => {
        if (!selectedDataIds.length) {
            if (downloadAll) downloadAll()
            return
        }

        const dataDownload = selectedDataIds.flatMap(id => {
            const data = list.find(d => String(d.id) === id)

            return data === undefined ? [] : [getDataUrl(data)]
        })

        switch (downloadType) {
            case "csv":
                downloadAsCSV(dataDownload.join("\n"), fileName())
                break
            case "json":
                downloadAsJSON(dataDownload, fileName())
                break
        }
    }

    const modalElement = () => {
        switch (dialog) {
            case "delete":
                return (
                    <Confirm
                        title="Confirm"
                        description={`Are you sure you want to delete these ${selectedDataIds.length} file(s)?`}
                        yesLabel={
                            isDeleting
                                ? "Deleting..."
                                : `Delete ${selectedDataIds.length} selected`
                        }
                        isProcessing={isDeleting}
                        onClickYes={deleteFiles}
                        onClickNo={hideDialog}
                        onClose={hideDialog}
                    />
                )
            case "download":
                return (
                    <DownloadTypeModal
                        name={{ singular: "data point", plural: "data points" }}
                        itemCount={selectedDataIds.length || totalItems}
                        onClose={hideDialog}
                        onConfirm={downloadData}
                        onSetType={setDownloadType}
                        hasFlatten={false}
                        type={downloadType}
                        willDownload={true}
                    />
                )
            default:
                return null
        }
    }

    const createProgressMessage = () => {
        const inProgress = uploadQueue.find(
            (item: any): item is UploadQueueItemInProgress =>
                item.status === UploadStatus.InProgress
        )

        return `Uploading "${
            inProgress && inProgress.name
        }"... (${uploadQueueCount} items left.)`
    }

    const renderDropZoneContent = ({
        getRootProps,
        getInputProps,
        isDragAccept
    }: DropzoneState): JSX.Element => {
        const message =
            uploadQueueCount > 0
                ? createProgressMessage()
                : `${isDragAccept ? "Drop" : "Drag"} files to upload here...`

        return (
            <div
                {...getRootProps()}
                className="h-48 bg-rhino-100 p-3 border-2 border-dashed border-rhino-500 rounded flex justify-center items-center"
            >
                <input {...getInputProps()} />
                <div>{message}</div>
            </div>
        )
    }
    const batchActions = [
        {
            name: "Download URLs",
            count: selectedDataIds.length,
            onClick: confirmDownload
        },
        {
            name: "Delete",
            count: selectedDataIds.length,
            onClick: confirmDelete
        }
    ]

    const downloadAllButton = (
        <Button
            color="darkviolet"
            variant="contained"
            onClick={confirmDownload}
            disabled={isDownloadingAll}
        >
            {isDownloadingAll ? "Downloading..." : "Download All URLs"}
        </Button>
    )
    const sidebar = isBatchMode ? (
        <BatchEditSidebar
            id={BATCH_EDIT_ID}
            itemIds={list.map(d => String(d.id))}
            actions={batchActions}
        />
    ) : (
        downloadAllButton
    )
    const handleToggleSelection = useCallback(
        (item: APIDataModel) => doToggleSelection([String(item.id)]),
        [doToggleSelection]
    )
    const paginationBar =
        pages > 1 ? (
            <ButtonGroup equalWidth={false} className="ml-2">
                {Array.from({ length: pages }).flatMap((_, i) => {
                    if (
                        pages > 10 &&
                        ![
                            0,
                            pages - 1,
                            pageNumber - 1,
                            pageNumber,
                            pageNumber + 1
                        ].includes(i)
                    ) {
                        return []
                    }

                    const handleClick = () => getList(i, sort)

                    return [
                        <Button
                            key={i}
                            variant="contained2"
                            color="plain"
                            size="xs"
                            className="w-auto inline-block"
                            disabled={pageNumber === i}
                            onClick={handleClick}
                        >
                            {i === 0 ? "Page " : ""}
                            {i + 1}
                        </Button>
                    ]
                })}
            </ButtonGroup>
        ) : null

    const handleChangeSort = (newSort: SortExpression<APIDataModel>) =>
        getList(pageNumber, newSort)

    const renderEmpty = () => <NothingMatched messageVariant="try" />

    return (
        <OrgDashboardBase
            orgUsername={orgUsername}
            fixedContentWidth={false}
            sidebar={sidebar}
            title="Storage"
            titleComponent="h1"
        >
            <div className="pb-4">
                <Uploader
                    stateKey={UPLOADER_ID}
                    multiple={true}
                    renderDropZoneContent={renderDropZoneContent}
                    squashSuccessMessage={false}
                    appendTimestamp={false}
                />
            </div>
            <TableHeader<APIDataModel>
                sortOptions={{
                    fields: [
                        {
                            field: "path",
                            label: "URL",
                            ascLabel: "Alphabetical",
                            descLabel: "Reverse alphabetical"
                        },
                        {
                            field: "created",
                            label: "Created",
                            ascLabel: "Earliest first",
                            descLabel: "Latest first"
                        }
                    ],
                    applied: sort,
                    onChange: handleChangeSort
                }}
                extraActions={paginationBar}
            />
            <Table
                className="w-full table-fixed"
                columns={columns}
                items={loading ? undefined : list}
                itemCount={list.length}
                getKey={getDataID}
                checkedItemIds={selectedDataIds}
                onToggleRowCheck={handleToggleSelection}
                renderEmpty={renderEmpty}
            />
            {modalElement()}
        </OrgDashboardBase>
    )
}

const mapStateToProps = (state: State): StateProps => {
    const batchEditEntry = state.client.interface.batchEdit[BATCH_EDIT_ID]
    const selectedDataIds =
        batchEditEntry === undefined ? [] : batchEditEntry.selected

    return {
        list: state.api.data.list,
        pages: state.api.data.pages,
        pageNumber: state.api.data.pageNumber,
        sort: state.api.data.sort,
        loading: state.api.data.loading,
        uploadQueue: state.client.interface.uploader[UPLOADER_ID] || [],
        selectedDataIds,
        isBatchMode: selectedDataIds.length > 0,
        totalItems: state.api.data.totalItemCount
    }
}

const mapDispatchToProps = (dispatch: MainThunkDispatch): DispatchProps => {
    return {
        getList: (page: number, sort: SortExpression<APIDataModel>) =>
            dispatch(getDataList(page, sort)),
        doDestroy: paths => dispatch(destroy(paths)),
        doClearSelection: () => dispatch(clearSelection(BATCH_EDIT_ID)),
        doToggleSelection: (itemIds: string[]) =>
            dispatch(toggleSelection(BATCH_EDIT_ID, itemIds)),
        doDownloadAllDataUrls: (fileName: string, type: DownloadType) =>
            dispatch(
                downloadAllDataUrls(
                    fileName,
                    type === "flat_csv" || type === "csv" ? "csv" : "json"
                )
            )
    }
}

export const DataSets = connect(
    mapStateToProps,
    mapDispatchToProps
)(DataSetsIsolated)
